'use client';

import { createContext, type ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { SITE } from '@/app/constants';
import { hysdk, initWechatJssdk } from '@/app/utils/client';
import { usePathname } from '@/i18n/routing';

interface WechatContextValue {
  /** 微信SDK是否已准备就绪 */
  wxSDKReady: boolean;
  /** 是否为微信环境 */
  isWechat: boolean;
  /** 微信SDK初始化错误信息 */
  error: string | null;
  /** 是否正在初始化 */
  isInitializing: boolean;
  /** 重新初始化微信SDK */
  reinitWechatSDK: () => Promise<void>;
}

const WechatContext = createContext<WechatContextValue | undefined>(undefined);

interface WechatProviderProps {
  children: ReactNode;
}

export function WechatProvider({ children }: WechatProviderProps) {
  const [wxSDKReady, setWxSDKReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const pathname = usePathname(); // 监听路由变化
  const initTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isWechat = hysdk.sniff.isWechat;

  const initSDK = useCallback(
    async (retryCount = 0) => {
      try {
        setError(null);
        setWxSDKReady(false);
        setIsInitializing(true);

        await initWechatJssdk();

        if (typeof window !== 'undefined' && window.wx) {
          window.wx.ready(() => {
            console.info('微信SDK准备就绪', `路由: ${pathname}`);
            setWxSDKReady(true);
            setError(null);
            setIsInitializing(false);
          });

          window.wx.error((err: unknown) => {
            console.error('微信 JSSDK 错误', err, `路由: ${pathname}`);
            const errorMessage = err instanceof Error ? err.message : '微信SDK初始化失败';

            // 重试
            if (
              retryCount < 2 &&
              (errorMessage.includes('invalid signature') || errorMessage.includes('config:fail'))
            ) {
              console.warn(`微信SDK初始化失败，尝试第${retryCount + 1}次重试...`);
              setTimeout(() => {
                initSDK(retryCount + 1);
              }, 1000); // 延迟1秒后重试
              return;
            }

            setError(errorMessage);
            setWxSDKReady(false);
            setIsInitializing(false);
          });
        }
      } catch (err) {
        console.error('初始化微信SDK失败', err, `路由: ${pathname}`);
        const errorMessage = err instanceof Error ? err.message : '微信SDK初始化失败';

        // 重试
        if (retryCount < 2) {
          console.warn(`微信SDK初始化异常，尝试第${retryCount + 1}次重试...`);
          setTimeout(() => {
            initSDK(retryCount + 1);
          }, 1000);
          return;
        }

        setError(errorMessage);
        setWxSDKReady(false);
        setIsInitializing(false);
      }
    },
    [pathname],
  );

  const reinitWechatSDK = async () => {
    setWxSDKReady(false);
    setError(null);
    await initSDK(0);
  };

  // 监听路由变化，防抖处理重新初始化SDK
  useEffect(() => {
    // 微信环境中初始化SDK
    if (!isWechat) return;

    // 清除之前的定时器
    if (initTimeoutRef.current) {
      clearTimeout(initTimeoutRef.current);
    }

    // 防抖处理：延迟300ms后再初始化，避免路由快速切换时频繁初始化
    initTimeoutRef.current = setTimeout(() => {
      console.info('路由变化，重新初始化微信SDK', `新路由: ${pathname}`);

      // 清除之前的URL缓存
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem('wechat_initial_url');
      }

      initSDK();
    }, 300);

    // 清理函数
    return () => {
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
      }
    };
  }, [isWechat, pathname, initSDK, SITE]);

  const value: WechatContextValue = {
    wxSDKReady,
    isWechat,
    error,
    isInitializing,
    reinitWechatSDK,
  };

  return <WechatContext.Provider value={value}>{children}</WechatContext.Provider>;
}

export function useWechat() {
  const context = useContext(WechatContext);
  if (context === undefined) {
    throw new Error('useWechat must be used within a WechatProvider');
  }
  return context;
}
